import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function ServicesOverview() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 'branding',
      title: 'Branding & Visual Identity',
      tagline: 'Crafting your unmistakable brand narrative.',
      icon: 'ri-palette-line',
      color: 'from-orange-500 to-red-500',
      path: '/services/branding',
      description: 'Transform your vision into a compelling brand story that resonates with your audience.'
    },
    {
      id: 'marketing',
      title: 'Data Driven Growth Marketing',
      tagline: 'Intelligent strategies for measurable market impact.',
      icon: 'ri-line-chart-line',
      color: 'from-red-500 to-pink-500',
      path: '/services/marketing',
      description: 'Leverage data analytics and AI to drive targeted growth and maximize ROI.'
    },
    {
      id: 'ai',
      title: 'AI & Agentic Solutions',
      tagline: 'Empowering efficiency through intelligent automation.',
      icon: 'ri-robot-line',
      color: 'from-pink-500 to-purple-500',
      path: '/services/ai',
      description: 'Automate processes and enhance decision-making with cutting-edge AI technology.'
    },
    {
      id: 'threed',
      title: '3D Multi Colour Development & Printing',
      tagline: 'Bringing your concepts to vibrant, tangible life.',
      icon: 'ri-3d-view-line',
      color: 'from-purple-500 to-blue-500',
      path: '/services/threed',
      description: 'Create stunning 3D models and prototypes with advanced multi-color printing.'
    },
    {
      id: 'web3',
      title: 'Web3 & Innovation Consultation',
      tagline: 'Guiding your brand into the next era of the internet.',
      icon: 'ri-global-line',
      color: 'from-blue-500 to-cyan-500',
      path: '/services/web3',
      description: 'Navigate the future of digital with blockchain, NFTs, and metaverse strategies.'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="services-overview"
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Discover Our Areas of 
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent"> Expertise</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Navigate through our specialized areas to find the perfect solution for your next big idea. 
            Click on any service category to learn more about how we can partner with you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Link
              key={service.id}
              to={service.path}
              className={`group block transition-all duration-700 delay-${index * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <div className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-orange-500/20 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/20 cursor-pointer h-full">
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                
                <div className={`relative w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center group-hover:animate-pulse`}>
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>

                <h3 className="text-xl font-bold text-white mb-4 text-center group-hover:text-orange-400 transition-colors duration-300">
                  {service.title}
                </h3>

                <p className="text-orange-400 text-center mb-4 font-medium">
                  {service.tagline}
                </p>

                <p className="text-gray-300 text-center text-sm leading-relaxed group-hover:text-white transition-colors duration-300">
                  {service.description}
                </p>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <i className="ri-arrow-right-line text-orange-400 text-xl"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}